import {tx, ptx, usePageText} from '../lib/page-text';
import { useAuth } from '../lib/auth-store';
import { RequestFeedback } from '../components/RequestFeedback';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api-client';
import { Code, CodeBlock } from '../components/ui';

interface Tester {
  id: string;
  token: string;
  url: string;
  createdAt: string;
  expiresAt: string;
  captureCount?: number;
}

interface Capture {
  id: string;
  method: string;
  headers: Record<string, string>;
  body: string;
  bodyBytes?:number;truncated?:boolean;rawBodyAvailable?:boolean;
  query: Record<string, any>;
  remoteIp: string;
  receivedAt: string;
}

function fmtTime(iso: string) { return new Date(iso).toLocaleTimeString(); }

export function WebhookTesterPage() {
  usePageText();
  const canManage=useAuth(state=>['OWNER','ADMIN'].includes(state.user?.role??''));
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['webhook-testers'], queryFn: async () => (await api.get('/me/webhook-tester')).data as Tester[] });
  const [selected, setSelected] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => (await api.post('/me/webhook-tester', {})).data as Tester,
    onSuccess: (t) => { setSelected(t.token); qc.invalidateQueries({ queryKey: ['webhook-testers'] }); },
  });

  const remove = useMutation({
    mutationFn: async (token: string) => api.post(`/me/webhook-tester/${token}/delete`, {}),
    onSuccess: () => { setSelected(null); qc.invalidateQueries({ queryKey: ['webhook-testers'] }); },
  });

  const captures = useQuery({
    queryKey: ['webhook-tester-captures', selected],
    queryFn: async () => (await api.get(`/me/webhook-tester/${selected}/captures`)).data as { captures: Capture[]; url: string; expiresAt: string; createdAt: string },
    enabled: !!selected,
    refetchInterval: 2_000,
  });

  if (list.isLoading) return <div className="muted">{tx("Loading…")}</div>;
  const testers = list.data ?? [];
  const current = testers.find((t) => t.token === selected);

  const publicBase = window.location.origin;

  return (
    <div>
      <div className="row-between" style={{ marginBottom: 24,flexWrap:'wrap',gap:12 }}>
        <div>
          <h1 className="h1">{tx("Webhook tester")}</h1>
          <p className="subtitle">{tx("Generate a temporary URL, point your webhook subscription at it, see exactly what we deliver.")}</p>
        </div>
        <button className="btn primary" onClick={() => create.mutate()} disabled={create.isPending||!canManage}>
          {create.isPending ? tx("Creating…") : tx("+ New tester")}
        </button>
      </div>

      <RequestFeedback pending={list.isPending} failed={list.isError} hasData={!!list.data} retry={()=>void list.refetch()}/>
      {(create.isError||remove.isError)&&<p className="error" role="alert">{(create.error as any)?.response?.data?.message??(remove.error as any)?.response?.data?.message??tx("Could not update tester. Try again.")}</p>}
      {list.data && testers.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <p className="muted" style={{ marginTop: 0 }}>{tx("No testers yet. Each tester gives you a unique URL that lasts 1 hour.")}</p>
          <button className="btn" onClick={() => create.mutate()} disabled={create.isPending||!canManage}>{tx("Create your first tester")}</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 16 }}>
          <div className="card" style={{ padding: 0 }}>
            <h3 style={{ margin: 16 }}>{tx("Active testers")}</h3>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {testers.map((t) => (
                <button
                  key={t.token}
                  onClick={() => setSelected(t.token)}
                  style={{
                    background: selected === t.token ? 'var(--surface-2)' : 'transparent',
                    border: 'none',
                    borderTop: '1px solid var(--border-soft)',
                    padding: '12px 16px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: 'inherit',
                    fontFamily: 'inherit',
                  }}
                >
                  <div className="mono" style={{ fontSize: 11, color: 'var(--text)' }}>...{t.token.slice(-12)}</div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                    {t.captureCount ?? 0}{tx(" requests · expires ")}{fmtTime(t.expiresAt)}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div style={{minWidth:0}}>
            {!current && <div className="card muted" style={{ textAlign: 'center', padding: 40 }}>{tx("Select a tester to view captures")}</div>}
            {current && (
              <>
                <div className="card" style={{ marginBottom: 12 }}>
                  <div className="row-between" style={{ marginBottom: 8 }}>
                    <h3 style={{ margin: 0 }}>{tx("Endpoint URL")}</h3>
                    <button className="btn outline sm danger" disabled={remove.isPending||!canManage} onClick={() => remove.mutate(current.token)}>{tx("Delete tester")}</button>
                  </div>
                  <Code value={current.url ?? `${publicBase}/v1/wt/${current.token}`} large />
                  <p className="muted" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>{tx("Send any HTTP request to this URL — POST, PUT, DELETE, GET — with any headers and body. We record the last 100 calls (up to 50 KiB of each body). Testers expire after one hour and disappear on API restart. Up to three testers per workspace are available.")}</p>
                  <div style={{ marginTop: 12 }}>
                    <CodeBlock value={`curl -X POST ${current.url ?? `${publicBase}/v1/wt/${current.token}`} \\
  -H "Content-Type: application/json" \\
  -H "X-Kardolive-Signature: sha256=..." \\
  -d '{"event":"stream.publish","streamId":"abc"}'`}/>
                  </div>
                </div>

                <div className="card" style={{ padding: 0 }}>
                  <div className="row-between" style={{ padding: 16 }}>
                    <h3 style={{ margin: 0 }}>{tx("Captures")}</h3>
                    <span className="muted" style={{ fontSize: 12 }}>{tx("refreshing every 2s")}</span>
                  </div>
                  <RequestFeedback pending={captures.isPending} failed={captures.isError} hasData={!!captures.data} retry={()=>void captures.refetch()}/>
                  {captures.data && captures.data.captures.length === 0 ? (
                    <div className="muted" style={{ padding: 24, textAlign: 'center' }}>{tx("Waiting for a request...")}</div>
                  ) : (
                    <div>
                      {captures.data?.captures.map((c) => (
                        <details key={c.id} style={{ borderTop: '1px solid var(--border-soft)' }}>
                          <summary style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>
                              <span className="badge info">{c.method}</span>
                              <span className="muted mono" style={{ fontSize: 11, marginLeft: 8 }}>{c.remoteIp}</span>
                              <span style={{ marginLeft: 8, fontSize: 13 }}>{c.body ? ptx("{v0} bytes", {v0: (c.bodyBytes??new TextEncoder().encode(c.body).length)}) : tx("no body")}</span>
                            </span>
                            <span className="muted" style={{ fontSize: 11 }}>{fmtTime(c.receivedAt)}</span>
                          </summary>
                          <div style={{ padding: '0 16px 16px' }}>
                            <h4 style={{ marginTop: 0, fontSize: 12, color: 'var(--text-dim)' }}>{tx("Headers")}</h4>
                            <pre className="mono" style={{ fontSize: 11, background: 'var(--surface-2)', padding: 8, borderRadius: 'var(--radius-sm)', overflow: 'auto' }}>
{Object.entries(c.headers).map(([k, v]) => `${k}: ${v}`).join('\n')}
                            </pre>
                            <p className="muted">{c.truncated?tx("Body truncated to 50 KiB; it cannot be used to verify the original signature."):c.rawBodyAvailable===false?tx("Parsed body shown; exact signature verification is unavailable."):tx("Exact captured request body.")}</p>
                            <h4 style={{ fontSize: 12, color: 'var(--text-dim)' }}>{tx("Body")}</h4>
                            <pre className="mono" style={{ fontSize: 11, background: 'var(--surface-2)', padding: 8, borderRadius: 'var(--radius-sm)', overflow: 'auto', maxHeight: 300 }}>
{c.body || '(empty)'}
                            </pre>
                          </div>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
